/**
 * Centralt: vilka React Query-nycklar invalideras när lager-cachen bustas,
 * + en liten store för senaste invalidering (visas i admin-widget).
 *
 * Importera `invalidateInventoryCaches` från ALLA platser (hook + manuella
 * trigger-knappar) så att listan endast underhålls här.
 *
 * INVALIDATION POLICY (uttrycklig):
 *  - 'success' | 'warning'  → invalidera (warning räknas som lyckad körning
 *      med non-fatal coverage-varningar; data är fortfarande färskare än cache).
 *  - 'error' | 'failed' | 'partial' → INVALIDERA INTE. En misslyckad run får
 *      inte ersätta en färsk cache med ofullständig data.
 *  - okänd / saknad run_status → INVALIDERA INTE. Hellre stale än felaktigt.
 *  - source: 'manual' | 'boot' (lokala triggers utan run_status) får alltid
 *      invalidera — då vet UI uttryckligen att en bust ska ske.
 */
import type { QueryClient } from "@tanstack/react-query";

export const INVENTORY_QUERY_PREFIXES: ReadonlyArray<string> = [
  "products",
  "product",
  "homepage",
  "bestsellers",
  "new-arrivals",
  "outlet",
  "category-products",
  "brand-products",
  "cross-sell",
  "also-bought",
];

export type InvalidationSource =
  | "realtime"
  | "fallback_poll"
  | "manual"
  | "boot";

export interface CacheSignalMeta {
  job_id?: string;
  /** Stable correlation id för en sync-körning (= jobId i edge-funktionen). */
  correlation_id?: string;
  tenant_id?: string;
  shop_id?: string;
  source?: string;
  run_status?: string;
  total_synced?: number;
  finished_at?: string;
  [key: string]: unknown;
}

export interface LastInvalidationRecord {
  version: number;
  at: string; // ISO timestamp
  source: InvalidationSource;
  prefixes: number;
  meta: CacheSignalMeta | null;
  correlation_id: string | null;
}

const LS_KEY = "zooi.inventory_cache.last_invalidation";

/** Statusar som FÅR trigga invalidering. */
export const ACCEPTED_RUN_STATUSES: ReadonlySet<string> = new Set([
  "success",
  "warning",
]);
/** Statusar som EXPLICIT FÖRBJUDS — får aldrig invalidera. */
export const REJECTED_RUN_STATUSES: ReadonlySet<string> = new Set([
  "error",
  "failed",
  "partial",
  "rejected",
]);

export type InvalidationDecision =
  | { allow: true; reason: "accepted_status" | "local_trigger" }
  | { allow: false; reason: "rejected_status" | "unknown_status" | "missing_status" };

/**
 * Pure decision function — testbar utan React/Query.
 * Källor utan server-meta ('manual'/'boot') får alltid passera.
 * För realtime/fallback krävs uttryckligen accepterad run_status.
 */
export function decideInvalidation(
  source: InvalidationSource,
  meta: CacheSignalMeta | null | undefined,
): InvalidationDecision {
  if (source === "manual" || source === "boot") {
    return { allow: true, reason: "local_trigger" };
  }
  const status = typeof meta?.run_status === "string" ? meta.run_status.toLowerCase() : null;
  if (!status) return { allow: false, reason: "missing_status" };
  if (REJECTED_RUN_STATUSES.has(status)) return { allow: false, reason: "rejected_status" };
  if (ACCEPTED_RUN_STATUSES.has(status)) return { allow: true, reason: "accepted_status" };
  return { allow: false, reason: "unknown_status" };
}

type Listener = (rec: LastInvalidationRecord) => void;
const listeners = new Set<Listener>();
let last: LastInvalidationRecord | null = readFromStorage();

function readFromStorage(): LastInvalidationRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as LastInvalidationRecord) : null;
  } catch {
    return null;
  }
}

function persist(rec: LastInvalidationRecord): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(rec));
  } catch {
    /* quota / privacy mode — ignore */
  }
}

export function getLastInvalidation(): LastInvalidationRecord | null {
  return last;
}

export function subscribeInvalidation(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** För enhetstester — nollställer inmemorystaten. */
export function __resetInvalidationStateForTests(): void {
  last = null;
  if (typeof window !== "undefined") {
    try { window.localStorage.removeItem(LS_KEY); } catch { /* noop */ }
  }
}

export interface InvalidateOpts {
  version: number;
  source: InvalidationSource;
  meta?: CacheSignalMeta | null;
}

export interface InvalidateResult {
  applied: boolean;
  decision: InvalidationDecision;
  record: LastInvalidationRecord | null;
}

/**
 * Invalidate ALL inventory-related React Query caches enligt policyn ovan.
 * Single source of truth — any code path that needs to bust inventory caches
 * MUST call this.
 */
export function invalidateInventoryCaches(
  queryClient: QueryClient,
  opts: InvalidateOpts,
): InvalidateResult {
  const decision = decideInvalidation(opts.source, opts.meta ?? null);
  if (!decision.allow) {
    return { applied: false, decision, record: null };
  }
  for (const prefix of INVENTORY_QUERY_PREFIXES) {
    queryClient.invalidateQueries({ queryKey: [prefix] });
  }
  // Also drop the in-memory products page + brand caches so the next fetch
  // hits the DB once and repopulates with fresh data.
  void import("@/lib/productsPageCache").then((m) => m.clearProductsPageCache());
  const correlationId =
    (opts.meta?.correlation_id as string | undefined) ??
    (opts.meta?.job_id as string | undefined) ??
    null;
  const rec: LastInvalidationRecord = {
    version: opts.version,
    at: new Date().toISOString(),
    source: opts.source,
    prefixes: INVENTORY_QUERY_PREFIXES.length,
    meta: opts.meta ?? null,
    correlation_id: correlationId,
  };
  last = rec;
  persist(rec);
  for (const fn of listeners) {
    try { fn(rec); } catch { /* listener errors must not block invalidation */ }
  }
  return { applied: true, decision, record: rec };
}
